import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { PrismIcon, PrismLoadingBar } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { IncrementFor } from 'frontend-utils';
import GeneralCardWithIllustration from 'lumen/components/general/card-with-illustration';
import MarketCustomReportingThumbnail from 'lumen/components/market/custom-reporting/thumbnail';
import type DestinationReportingIndexController from 'lumen/controllers/destination/reporting/index';

interface DestinationReportingIndexSignature {
  Args: {
    controller: DestinationReportingIndexController;
    model: unknown;
  };
}

<template>
  <div class="custom-reporting__page flex w-full flex-col gap-800 p-800">
    {{#if @controller.model.recentDashboards.isRunning}}
      <div class="loading-bar-container">
        <PrismLoadingBar />
      </div>
    {{/if}}
    <div>
      <h2 class="mb-400" data-test-id="recently-viewed-header">
        {{t "custom_reporting.recently_viewed"}}
      </h2>
      <div class="grid grid-cols-4 gap-400">
        {{#if @controller.model.recentDashboards.isRunning}}
          <IncrementFor @times={{4}}>
            <MarketCustomReportingThumbnail @isLoading={{true}} />
          </IncrementFor>
        {{else}}
          {{#each @controller.recentlyViewedDashboards as |dashboard|}}
            <LinkTo
              @model={{dashboard}}
              @route="destination.reporting.dashboard"
            >
              <MarketCustomReportingThumbnail
                @imageAlt={{dashboard.title}}
                @imageURL={{dashboard.thumbnailUrl}}
                @subtitle={{t
                  "custom_reporting.edited_timestamp"
                  timestamp=dashboard.updateTimestamp
                }}
                @title={{dashboard.title}}
                data-test-id="recently-viewed-dashboard-thumbnail"
              />
            </LinkTo>
          {{/each}}
        {{/if}}
      </div>
    </div>
    <div>
      <div class="mb-400">
        <h2 data-test-id="learn-header">{{t "custom_reporting.learn"}}</h2>
      </div>
      <div class="grid grid-cols-4 gap-400">
        {{#each @controller.helpArticles as |article|}}
          <GeneralCardWithIllustration
            @description={{article.description}}
            @illustration={{article.illustration}}
            @title={{article.title}}
            data-test-id="learn-card"
          >
            <:footer>
              <a
                class="prism-link prism-link--standalone prism-link--small"
                data-test-id="learn-card-link"
                href={{article.cta.url}}
                rel="noopener noreferrer"
                target="_blank"
              >
                {{article.cta.label}}
                <PrismIcon @color="inherit" @name="link-out" @size="small" />
              </a>
            </:footer>
          </GeneralCardWithIllustration>
        {{/each}}
      </div>
    </div>
  </div>
</template> satisfies TOC<DestinationReportingIndexSignature>;
